import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaySuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("confirming");

    useEffect(() => {
        if (!sessionId) {
            setState("unknown");
            return undefined;
        }
        let attempts = 0;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState("paid");
                    return;
                }
                if (data.payment_status === "processing") {
                    setState("processing");
                    return;
                }
                attempts += 1;
                if (attempts < 10) setTimeout(poll, 2000);
                else setState("processing");
            } catch (e) {
                attempts += 1;
                if (attempts < 10) setTimeout(poll, 2000);
                else setState("unknown");
            }
        };
        poll();
        return undefined;
    }, [sessionId]);

    return (
        <>
            <PageMeta title="Payment Confirmation — Nationwide Strategies LLC" description="Payment confirmation." />
            <section data-testid="pay-success-page" className="pt-32 sm:pt-40">
                <div className="mx-auto max-w-2xl px-4 pb-28 sm:px-6">
                    {state === "confirming" && (
                        <>
                            <Clock size={20} className="text-blue-400" aria-hidden="true" />
                            <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-slate-100">Confirming your payment…</h1>
                            <p data-testid="pay-confirming-message" className="mt-6 text-sm leading-relaxed text-slate-400">
                                One moment while we verify with the payment processor.
                            </p>
                        </>
                    )}
                    {state === "paid" && (
                        <>
                            <CheckCircle2 size={20} className="text-blue-400" aria-hidden="true" />
                            <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-slate-100">Payment received.</h1>
                            <p data-testid="pay-received-message" className="mt-6 text-sm leading-relaxed text-slate-400">
                                Thank you. Your payment has been completed and a receipt is on its way to your email.
                                Your reference is <span className="font-mono text-slate-200">{sessionId?.slice(-12)}</span>.
                            </p>
                        </>
                    )}
                    {state === "processing" && (
                        <>
                            <Clock size={20} className="text-blue-400" aria-hidden="true" />
                            <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-slate-100">Payment in process.</h1>
                            <p data-testid="pay-processing-message" className="mt-6 text-sm leading-relaxed text-slate-400">
                                Your payment was submitted successfully. Bank (ACH) transfers are confirmed when the
                                transfer clears, typically within a few business days. Your reference is{" "}
                                <span className="font-mono text-slate-200">{sessionId?.slice(-12)}</span>.
                            </p>
                        </>
                    )}
                    {state === "unknown" && (
                        <>
                            <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-slate-100">Almost done.</h1>
                            <p data-testid="pay-unknown-message" className="mt-6 text-sm leading-relaxed text-slate-400">
                                We could not automatically confirm this payment just now. If you completed checkout,
                                your payment is fine; call us with any question and reference your invoice number.
                            </p>
                        </>
                    )}
                    <Link
                        to="/"
                        data-testid="pay-success-home-link"
                        className="mt-12 inline-flex items-center gap-3 border border-slate-700 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-200 transition-colors duration-300 hover:border-blue-500 hover:text-white"
                    >
                        Return to the Site
                    </Link>
                </div>
            </section>
        </>
    );
}
